import { PaymentMethodIdEnum, Payments, TOrder } from "@core/types";
import { IonIcon } from "@ionic/react";
import { checkmarkCircle, closeCircle, time, cube, hourglass, paperPlane } from "ionicons/icons";
import React from "react";
import { useTranslation } from "react-i18next";
import i18n from "../../../../i18n";

type OrderStatusProps = {
  orderStatus: TOrder["status"];
  paymentStatus: Payments["status"];
  paymentMethod: TOrder["paymentMethod"];
  orderPaymentStatus: TOrder["paymentStatus"];
};

const ORDER_STATUSES_TO_HIDE_WAITING_FOR_PAYMENT = ["cancelled", "processing", "new"];

const getStatusConfig = (
  orderStatus: TOrder["status"],
): { icon: any; color: string; title: string; description: string } => {
  const t = i18n.t;
  const translatedOrderStatus = t(orderStatus as string);

  switch (orderStatus) {
    case "completed":
      return {
        icon: checkmarkCircle,
        color: "[#89D55B]",
        title: translatedOrderStatus || t("Order is completed"),
        description: "",
      };
    case "cancelled":
      return {
        icon: closeCircle,
        color: "[var(--ion-color-danger)]",
        title: translatedOrderStatus || t("Order is cancelled"),
        description: "Оформите новый заказ",
      };
    case "new":
    case "processing":
    case "pending":
      return {
        icon: cube,
        color: "[#FCA42A]",
        title: translatedOrderStatus || t("Order is being processed"),
        description: "Мы готовим ваш заказ",
      };
    case "pending-delivery":
      return {
        icon: paperPlane,
        color: "[#FCA42A]",
        title: translatedOrderStatus || t("Order is waiting for delivery"),
        description: "Скоро будет передан в доставку",
      };
    case "out-for-delivery":
      return {
        icon: time,
        color: "[#89D55B]",
        title: translatedOrderStatus || t("Order is on the way"),
        description: "Ваш заказ уже доставляется",
      };
    case "delivered":
      return {
        color: "[#89D55B]",
        icon: checkmarkCircle,
        description: t("Your order is delivered"),
        title: translatedOrderStatus || t("Order is delivered"),
      };
    case "awaiting-payment":
      return {
        icon: hourglass,
        color: "[#FCA42A]",
        title: translatedOrderStatus || t("Order is awaiting payment"),
        description: t("Order is awaiting payment"),
      };
    case "assembly":
    case "assembly-started":
      return {
        icon: hourglass,
        color: "[#FCA42A]",
        title: translatedOrderStatus || t("Order is assembly started"),
        description: t("Order is assembly started"),
      };
    default:
      return {
        icon: hourglass,
        color: "[#FCA42A]",
        title: translatedOrderStatus || t(orderStatus as string),
        description: t("Order is in process"),
      };
  }
};

const OrderStatus = ({ orderStatus, paymentMethod, paymentStatus, orderPaymentStatus }: OrderStatusProps) => {
  const { t } = useTranslation();
  const { color, icon, title, description } = getStatusConfig(orderStatus);

  const isPaid = paymentStatus === "succeeded" || orderPaymentStatus === "PAID";

  const isWaitingForPayment = !ORDER_STATUSES_TO_HIDE_WAITING_FOR_PAYMENT.includes(orderStatus ?? "");

  const shouldShowWaitingForPayment =
    isWaitingForPayment &&
    paymentMethod?.type === PaymentMethodIdEnum.cash &&
    !ORDER_STATUSES_TO_HIDE_WAITING_FOR_PAYMENT.includes(orderStatus ?? "");

  return (
    <div className="flex flex-col items-center justify-center">
      <div className="flex justify-center mb-3">
        <IonIcon
          icon={icon}
          className={`text-${color} text-[45px]`}
          color={icon === checkmarkCircle ? "success" : undefined}
        />
      </div>
      <h1 className="text-xl font-semibold text-center">{title}</h1>
      <p className="text-base font-medium text-center">{description}</p>
      {isPaid ? (
        <div className="mt-2 text-center">
          <span className="px-3 py-1 bg-[var(--ion-color-success)] text-white rounded-full text-sm font-medium">
            {t("Paid")}
          </span>
        </div>
      ) : shouldShowWaitingForPayment ? (
        <div className="mt-2 text-center">
          <span className="px-3 py-1 bg-[var(--ion-color-primary)] text-white rounded-full text-sm font-medium">
            {t("Waiting for payment")}
          </span>
        </div>
      ) : null}
    </div>
  );
};

export default OrderStatus;
